import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { GlobeAltIcon, PencilIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Input,
  Link,
  Spinner,
} from '@heroui/react';
import { useEffect, useMemo, useState } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AccountDeactivatedBadge from '@/app/components/account/account-deactivated-badge.tsx';
import AccountFlagBadge from '@/app/components/account/account-flag-badge.tsx';
import { NoData } from '@/app/components/no-data.tsx';
import OrganizationReportingDialog from '@/app/components/organization/organization-reporting-dialog.tsx';
import OrganizationSearchForm from '@/app/components/organization/organization-search-form.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import { Paginator, usePagination } from '@/app/components/paginator.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import { Heading } from '@/primitives/heading.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import { useFetchVisibleAccountsQuery } from '@/slices/account-api-slice.ts';
import { useFetchOrganizationSearchQuery } from '@/slices/organization-api-slice.ts';
import { hasAnyPermission, hasPermission } from '../../../types/constants.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';

const OrganizationList = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, isLoading } = useFetchOrganizationSearchQuery(searchParams.toString());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);

  const { sort, setSort, sortData } = useSort({
    data,
  });
  const paginator = usePagination({
    data: sortData,
    resourceName: 'organizations',
  });
  const { pagedData } = paginator;

  const { showPageLoading, hidePageLoading } = usePageLoading();

  const onOpenDrawer = (value: any) => {
    setSelectedOrg(value);
    setDrawerOpen(true);
  };

  const onCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedOrg(null);
  };

  useEffect(() => {
    (isLoading ? showPageLoading : hidePageLoading)('organization-list');
  }, [isLoading]);

  return (
    <>
      <Heading className='pb-5' icon={<GlobeAltIcon className='size-7' />}>
        Organizations
      </Heading>
      <div className='flex justify-between pb-5'>
        <OrganizationSearchForm />
        {user?.userType === UserTypeEnum.Admin && (
          <div>
            <Button
              color='primary'
              variant='solid'
              onPress={() => navigate('new')}
              startContent={<PlusIcon className='size-5' />}
            >
              Add Organization
            </Button>
          </div>
        )}
      </div>
      <Table currentSort={sort} setCurrentSort={setSort}>
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='organization.name'>Name</TableHeader>
            <TableHeader sortColumn='organization.address.city'>Location</TableHeader>
            <TableHeader sortColumn='organization.emailAddress'>Org Contact</TableHeader>
            <TableHeader className='text-center' sortColumn='organization.accountCount'>
              Accounts
            </TableHeader>
            <TableHeader className='text-right'>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedData?.length ? (
            pagedData.map(value => {
              const canEditOrg =
                user?.userType === UserTypeEnum.Admin ||
                (user?.userType === UserTypeEnum.StaffMember &&
                  hasAnyPermission(
                    user as StaffMemberModel,
                    StaffRoleEnum.OrganizationAdministrator,
                    value.accounts.map(x => x.account.id),
                  ));
              return (
                <TableRow
                  key={value.organization.id}
                  className='cursor-pointer hover:bg-gray-50'
                  onClick={() => onOpenDrawer(value)}
                >
                  <TableCell>{value.organization.name}</TableCell>
                  <TableCell>
                    {value.organization.address.city},{' '}
                    {value.organization.address.state.abbreviation}
                  </TableCell>
                  <TableCell>
                    <div className='flex-col gap-1'>
                      <TextLink
                        onClick={event => event.stopPropagation()}
                        href={`mailto:${value.organization.emailAddress}`}
                      >
                        {value.organization.emailAddress}
                      </TextLink>
                      <p>{formatPhoneNumber(value.organization.phoneNumber)}</p>
                    </div>
                  </TableCell>
                  <TableCell className='text-center'>
                    <div className='rounded-full size-8 flex mx-auto items-center justify-center bg-default-200 text-default-700 font-semibold'>
                      {value.organization.accountCount}
                    </div>
                  </TableCell>
                  <TableCell className='text-right'>
                    <div className='flex gap-1 items-center justify-end'>
                      {canEditOrg && (
                        <>
                          <Button
                            title='Edit'
                            size='sm'
                            onClick={e => {
                              e.stopPropagation();
                              navigate(`/organization/edit/${value.organization.key}`);
                            }}
                            color='default'
                            variant='flat'
                          >
                            <PencilIcon className='size-4' />
                            Edit Org
                          </Button>
                          <Button
                            title='Add account'
                            size='sm'
                            color='primary'
                            variant='flat'
                            onClick={e => {
                              e.stopPropagation();
                              navigate(`/account/new/${value.organization.key}`);
                            }}
                          >
                            <PlusIcon className='size-4' />
                            Add Account
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Paginator {...paginator} />
      <Drawer
        hideCloseButton
        isOpen={drawerOpen}
        onClose={onCloseDrawer}
        placement='right'
        size='lg'
      >
        <DrawerContent>
          <DrawerHeader className='flex items-center justify-between border-b bg-gradient-primary text-primary-950'>
            <h2 className='text-xl font-semibold'>Organization Details</h2>
            <Button isIconOnly variant='light' onPress={onCloseDrawer} color='default'>
              <XMarkIcon className='size-5' />
            </Button>
          </DrawerHeader>
          <DrawerBody className='p-6'>
            {selectedOrg && (
              <OrganizationDrawerContent
                organizationKey={selectedOrg.organization.key}
                organization={selectedOrg.organization}
              />
            )}
          </DrawerBody>
          <DrawerFooter className='border-t'>
            <div className='flex justify-end gap-2 w-full'>
              {selectedOrg &&
                (user?.userType === UserTypeEnum.Admin ||
                  (user?.userType === UserTypeEnum.StaffMember &&
                    hasAnyPermission(
                      user as StaffMemberModel,
                      StaffRoleEnum.OrganizationAdministrator,
                      selectedOrg.accounts.map(x => x.account.id),
                    ))) && (
                  <>
                    <Button
                      size='sm'
                      color='default'
                      variant='flat'
                      onPress={() => {
                        navigate(`/organization/edit/${selectedOrg.organization.key}`);
                        onCloseDrawer();
                      }}
                      startContent={<PencilIcon className='size-4' />}
                    >
                      Edit Organization
                    </Button>
                    <Button
                      size='sm'
                      color='primary'
                      variant='flat'
                      onPress={() => {
                        navigate(`/account/new/${selectedOrg.organization.key}`);
                        onCloseDrawer();
                      }}
                      startContent={<PlusIcon className='size-4' />}
                    >
                      Add Account
                    </Button>
                  </>
                )}
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const OrganizationDrawerContent = ({
  organizationKey,
  organization,
}: {
  organizationKey: string;
  organization: any;
}) => {
  const { user, isImpersonating } = useAuth();
  const { data: accounts, isLoading } = useFetchVisibleAccountsQuery({ organizationKey });
  const [searchQuery, setSearchQuery] = useState('');
  const [reportingOpen, setReportingOpen] = useState(false);

  const isAdminViewer = user?.userType === UserTypeEnum.Admin && !isImpersonating;

  const filteredAccounts = useMemo(() => {
    if (!accounts) {
      return [];
    }
    if (!searchQuery.trim()) {
      return accounts;
    }

    const query = searchQuery.toLowerCase();
    return accounts.filter(account => {
      const accountName = account.accountName.toLowerCase();
      const city = account.address.city.toLowerCase();
      const specialties = account.specialties.join(', ').toLowerCase();
      return accountName.includes(query) || city.includes(query) || specialties.includes(query);
    });
  }, [accounts, searchQuery]);

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <h3 className='text-sm font-semibold text-gray-900 mb-2'>{organization.name}</h3>
        <div className='flex flex-col gap-1 text-sm'>
          <div>
            <span className='font-medium text-gray-700'>Email: </span>
            <TextLink href={`mailto:${organization.emailAddress}`}>
              {organization.emailAddress}
            </TextLink>
          </div>
          <div>
            <span className='font-medium text-gray-700'>Phone: </span>
            <span className='text-gray-600'>{formatPhoneNumber(organization.phoneNumber)}</span>
          </div>
          <div>
            <span className='font-medium text-gray-700'>Location: </span>
            <span className='text-gray-600'>
              {organization.address.city}, {organization.address.state.abbreviation}
            </span>
          </div>
        </div>
      </div>

      {isAdminViewer && (
        <Button color='secondary' variant='flat' onPress={() => setReportingOpen(true)}>
          Reporting Data [admin]
        </Button>
      )}

      <div>
        <h3 className='text-sm font-semibold text-gray-900 mb-3'>
          Accounts ({accounts?.length || 0})
        </h3>

        {isLoading && (
          <div className='flex justify-center py-4'>
            <Spinner size='sm' />
          </div>
        )}

        {!isLoading && accounts && accounts.length > 0 && (
          <>
            <Input
              size='sm'
              placeholder='Search accounts by name, location, or specialty...'
              value={searchQuery}
              onValueChange={setSearchQuery}
              isClearable
              classNames={{
                base: 'max-w-full mb-3',
                inputWrapper: 'h-9',
              }}
            />
            {filteredAccounts.length > 0 ? (
              <div className='space-y-2'>
                {filteredAccounts.map(account => {
                  const isDeactivated = !!account.deactivatedAt;
                  const canManageAccount =
                    user?.userType === UserTypeEnum.Admin ||
                    (user?.userType === UserTypeEnum.StaffMember &&
                      hasPermission(
                        user as StaffMemberModel,
                        StaffRoleEnum.SiteAdministrator,
                        account.id,
                      ));
                  const showAccountActions = canManageAccount && !isDeactivated;

                  return (
                    <Card key={account.id} shadow='none' className='border border-gray-200'>
                      <CardBody className='p-3'>
                        <div className='flex flex-col gap-2'>
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <div className='flex items-center gap-2'>
                                {isDeactivated ? (
                                  <span className='text-sm font-medium text-gray-900'>
                                    {account.accountName}
                                  </span>
                                ) : (
                                  <TextLink
                                    href={`/account/info/${account.key}`}
                                    className='text-sm font-medium text-gray-900'
                                  >
                                    {account.accountName}
                                  </TextLink>
                                )}
                                <AccountFlagBadge accountFlagId={account.accountFlagId} />
                                <AccountDeactivatedBadge deactivatedAt={account.deactivatedAt} />
                              </div>
                              <div className='text-xs text-gray-500 mt-0.5'>
                                {account.accountType}
                              </div>
                            </div>
                          </div>
                          <div className='flex flex-col gap-1 text-xs text-gray-600'>
                            <div>
                              <span className='font-medium'>Location: </span>
                              {account.address.city}, {account.address.state.abbreviation}
                            </div>
                            {account.specialties?.length > 0 && (
                              <div className='flex flex-wrap gap-1 mt-1'>
                                {account.specialties.map(specialty => (
                                  <Chip key={specialty} size='sm' variant='flat' color='primary'>
                                    {specialty}
                                  </Chip>
                                ))}
                              </div>
                            )}
                          </div>
                          {showAccountActions && (
                            <div className='flex items-center gap-2 mt-2 pt-2 border-t'>
                              <Button
                                size='sm'
                                variant='flat'
                                color='default'
                                as={Link}
                                href={`/account/edit/${account.key}`}
                              >
                                <PencilIcon className='size-3' />
                                Edit
                              </Button>
                              <Button
                                size='sm'
                                variant='flat'
                                color='default'
                                as={Link}
                                href={`/account/settings/${account.key}`}
                              >
                                <Cog6ToothIcon className='size-3' />
                                Settings
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className='text-xs text-gray-500 text-center py-4'>
                No accounts found matching "{searchQuery}"
              </p>
            )}
          </>
        )}

        {!isLoading && (!accounts || accounts.length === 0) && (
          <p className='text-xs text-gray-500'>No accounts available</p>
        )}
      </div>

      <OrganizationReportingDialog
        isOpen={reportingOpen}
        setIsOpen={setReportingOpen}
        organizationKey={organizationKey}
        organizationName={organization.name}
      />
    </div>
  );
};

export default OrganizationList;
